/**
 * AutonomyDialPane — per-capability propose/execute axes.
 *
 * v0.5.1 read-only: renders each capability from .meeri.yaml with its
 * current propose/execute settings + graduation criteria. Toggling in
 * the UI is a v0.5.2 feature (requires a password-gated server-side
 * endpoint to rewrite .meeri.yaml + emit AUTONOMY_CHANGED ticket).
 *
 * The goal is TRANSPARENCY: the user can see at a glance which
 * capabilities Meeri is trusted to run autonomously vs which require
 * manual approval.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';

export function AutonomyDialPane() {
  const [a, setA] = useState<api.Autonomy | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.morningCoffee
      .autonomy()
      .then(setA)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-ink-mute">loading…</div>;
  if (!a) return <div className="text-sm text-warn">Autonomy config unavailable</div>;

  const autoAuto = a.capabilities.filter(
    (c) => c.propose === 'auto' && c.execute === 'auto',
  ).length;
  const autoManual = a.capabilities.filter(
    (c) => c.propose === 'auto' && c.execute === 'manual',
  ).length;
  const manualManual = a.capabilities.filter(
    (c) => c.propose === 'manual' && c.execute === 'manual',
  ).length;

  return (
    <div className="space-y-3">
      <div className="card p-3">
        <div className="text-xs text-ink-mute mb-1">
          intent <span className="num">{a.intent_version}</span> · meeri{' '}
          <span className="num">{a.meeri_version}</span>
        </div>
        <div className="flex gap-3 text-xs mt-2">
          <Stat label="fully autonomous" n={autoAuto} accent="accent" />
          <Stat label="propose-only" n={autoManual} accent="" />
          <Stat label="manual" n={manualManual} accent="" />
        </div>
      </div>

      <div className="card p-3">
        <div className="text-xs text-ink-mute mb-2">
          Capabilities ({a.capabilities.length})
        </div>
        <table className="w-full text-xs">
          <thead className="text-ink-mute text-left">
            <tr>
              <th className="pb-1">capability</th>
              <th className="pb-1">propose</th>
              <th className="pb-1">execute</th>
              <th className="pb-1">graduation</th>
            </tr>
          </thead>
          <tbody>
            {a.capabilities.map((c) => (
              <tr key={c.id} className="border-t border-edge align-top">
                <td className="py-2">
                  <div className="font-semibold">{c.id}</div>
                  {c.description && (
                    <div className="text-ink-mute text-[11px]">
                      {c.description}
                    </div>
                  )}
                </td>
                <td className="py-2">
                  <AxisPill axis={c.propose} />
                </td>
                <td className="py-2">
                  <AxisPill axis={c.execute} />
                </td>
                <td className="py-2 text-ink-mute text-[11px]">
                  {c.graduation_criteria ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {a.halt_contacts.length > 0 && (
        <div className="card p-3">
          <div className="text-xs text-ink-mute mb-2">Halt notifications</div>
          <ul className="space-y-1 text-xs">
            {a.halt_contacts.map((c, i) => (
              <li key={i}>
                <span className="font-semibold">{c.email}</span>
                {c.on.length > 0 && (
                  <span className="text-ink-mute ml-2 num text-[10px]">
                    on: {c.on.join(', ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[11px] text-ink-mute italic">
        v0.5.1 read-only. Toggling requires password-gated server endpoint
        (v0.5.2). Amendment protocol: emit AUTONOMY_CHANGED ticket +
        manual rewrite of <code className="num">.meeri.yaml</code>.
      </div>
    </div>
  );
}

function AxisPill({ axis }: { axis: 'auto' | 'manual' }) {
  if (axis === 'auto') {
    return (
      <span className="pill text-[10px] text-accent border-accent">AUTO</span>
    );
  }
  return <span className="pill text-[10px]">manual</span>;
}

function Stat({
  label,
  n,
  accent,
}: {
  label: string;
  n: number;
  accent: string;
}) {
  return (
    <div className="flex-1">
      <div
        className={`num text-xl font-bold ${accent ? `text-${accent}` : ''}`}
      >
        {n}
      </div>
      <div className="text-[10px] text-ink-mute">{label}</div>
    </div>
  );
}
